import { isObject, isString } from 'lodash'

import type {
  BillingCompleteness,
  BillingCompletenessItem,
  BillingDraft,
  BillingDraftChange,
  BillingDraftFields,
  BillingDraftWarning,
  BillingTaxGuidance
} from './types'

/**
 * Coerces a raw `billing_draft` payload (live SSE frame or the persisted `submit_billing_draft`
 * tool input) into a well-formed {@link BillingDraft}. Returns null when the payload is not a
 * recognisable draft so a malformed frame is dropped instead of crashing the transcript.
 */
export default function parseBillingDraft(raw: unknown, fallbackId: string): BillingDraft | null {
  if (!isObject(raw)) return null
  const value = raw as Record<string, unknown>

  const entity = value.entity
  if (entity !== 'product' && entity !== 'agreement') return null

  const mode = value.mode === 'update' ? 'update' : 'create'
  const fields = isObject(value.fields) ? (value.fields as BillingDraftFields) : {}

  return {
    draftId: isString(value.draftId) && value.draftId ? value.draftId : fallbackId,
    entity,
    mode,
    targetId: isString(value.targetId) ? value.targetId : undefined,
    title: isString(value.title) ? value.title : '',
    summary: isString(value.summary) ? value.summary : '',
    fields,
    changes: Array.isArray(value.changes) ? value.changes.map(parseChange).filter(isDefined) : [],
    warnings: Array.isArray(value.warnings) ? value.warnings.map(parseWarning).filter(isDefined) : [],
    taxGuidance: parseTaxGuidance(value.taxGuidance),
    completeness: parseCompleteness(value.completeness)
  }
}

function parseChange(raw: unknown): BillingDraftChange | undefined {
  if (!isObject(raw)) return undefined
  const value = raw as Record<string, unknown>
  if (!isString(value.path)) return undefined
  return {
    path: value.path,
    label: isString(value.label) ? value.label : value.path,
    from: value.from,
    to: value.to,
    reason: isString(value.reason) ? value.reason : undefined
  }
}

function parseWarning(raw: unknown): BillingDraftWarning | undefined {
  if (!isObject(raw)) return undefined
  const value = raw as Record<string, unknown>
  if (!isString(value.message)) return undefined
  const severity = value.severity
  return {
    severity: severity === 'error' || severity === 'info' ? severity : 'warning',
    code: isString(value.code) ? value.code : undefined,
    message: value.message,
    path: isString(value.path) ? value.path : undefined
  }
}

function parseTaxGuidance(raw: unknown): BillingTaxGuidance | null {
  if (!isObject(raw)) return null
  const value = raw as Record<string, unknown>
  const behavior = value.suggestedTaxBehavior
  return {
    suggestedTaxCode: isString(value.suggestedTaxCode) ? value.suggestedTaxCode : undefined,
    suggestedTaxBehavior: behavior === 'INCLUSIVE' || behavior === 'EXCLUSIVE' ? behavior : undefined,
    rationale: isString(value.rationale) ? value.rationale : '',
    considerations: Array.isArray(value.considerations) ? value.considerations.filter(isString) : [],
    sources: Array.isArray(value.sources) ? value.sources.filter(isString) : undefined
  }
}

function parseCompleteness(raw: unknown): BillingCompleteness | null {
  if (!isObject(raw)) return null
  const value = raw as Record<string, unknown>
  const score = typeof value.score === 'number' ? Math.min(100, Math.max(0, Math.round(value.score))) : 0
  const items = Array.isArray(value.items) ? value.items.map(parseCompletenessItem).filter(isDefined) : []
  return { score, items }
}

function parseCompletenessItem(raw: unknown): BillingCompletenessItem | undefined {
  if (!isObject(raw)) return undefined
  const value = raw as Record<string, unknown>
  if (!isString(value.key)) return undefined
  const status = value.status
  return {
    key: value.key,
    status: status === 'done' || status === 'warning' ? status : 'todo',
    message: isString(value.message) ? value.message : ''
  }
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}
